import React from "react";
import { Route, RouteWithChildren } from "./getRoutingHooks";
import { RouteID } from "./routes";
import {
  createBrowserRouter,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router";
import Landing from "../pages/landing/index";
import StaticPage from "../pages/index";
import VisApp from "../visualization/index";
import * as Sentry from "@sentry/react";
import ErrorPage from "../pages/ErrorPage";
import AtlasUIShowcase from "../pages/atlas-ui";
import PackagesLayout from "../pages/packages/PackagesLayout";
import PackagesLanding from "../pages/packages/index";
import TokensPage from "../pages/packages/sections/TokensPage";
import PackageDataPage from "../pages/packages/sections/DataPage";
import UIPage from "../pages/packages/sections/UIPage";
import ControlsPage from "../pages/packages/sections/ControlsPage";
import VizPage from "../pages/packages/sections/VizPage";
import ArchitecturePage from "../pages/packages/sections/ArchitecturePage";

import Placeholder from "../pages/Placeholder";
import About from "../pages/about/index";
import FAQ from "../pages/faq/index";
import Glossary from "../pages/glossary";
import TradeDataMethodologyPage from "../pages/trade-data-methodology/FullWidthPage";
import TradeDataMethodologyPresentation from "../pages/trade-data-methodology/PresentationFullWidthPage";
import Treemap from "../visualization/connectedCharts/ConnectedTreemap";
import Geomap from "../visualization/connectedCharts/ConnectedGeomap";
import OverTime from "../visualization/connectedCharts/ConnectedOvertime";
import MarketShare from "../visualization/connectedCharts/ConnectedMarketShare";
import GrowthPotential from "../visualization/connectedCharts/ConnectedGrowth";
import ProductSpace from "../visualization/connectedCharts/ConnectedProductSpace";
import { Helmet } from "react-helmet-async";
import DataPage from "../pages/data";
import { migrateOldUrlToNewFormat } from "./urlMigration";
import { useEffect } from "react";

const RedirectToTreemap = () => {
  const location = useLocation();
  return <Navigate to={`/explore/treemap${location.search}`} replace />;
};

// Wrapper component that handles URL migration for backward compatibility
// Only runs ONCE on initial page load, not on every URL change
const URLMigrationWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const { needsMigration, migratedParams } = migrateOldUrlToNewFormat(params);

    if (needsMigration) {
      // Replace current URL with migrated version
      navigate(
        {
          pathname: location.pathname,
          search: migratedParams.toString(),
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  return <>{children}</>;
};

export const routes: Array<Route | RouteWithChildren> = [
  {
    // Root error boundary — catches unhandled errors from any route
    errorElement: <ErrorPage />,
    children: [
      // Landing page (rewrite)
      {
        id: RouteID.Landing,
        path: "/",
        element: <Landing />,
      },

      // Explore routes (rewrite visualizations)
      {
        id: RouteID.Explore,
        path: "/explore",
        element: (
          <URLMigrationWrapper>
            <VisApp />
          </URLMigrationWrapper>
        ),
        children: [
          {
            index: true,
            element: <RedirectToTreemap />,
          },
          {
            id: RouteID.GeoMap,
            path: "/explore/geomap",
            element: (
              <>
                <Helmet>
                  <meta property="og:image" content="/og/share_geo.png" />
                </Helmet>
                <Geomap />
              </>
            ),
          },
          {
            id: RouteID.OverTime,
            path: "/explore/overtime",
            element: (
              <>
                <Helmet>
                  <meta property="og:image" content="/og/share_stack.png" />
                </Helmet>
                <OverTime />
              </>
            ),
          },
          {
            id: RouteID.MarketShare,
            path: "/explore/marketshare",
            element: (
              <>
                <Helmet>
                  <meta property="og:image" content="/og/share_market.png" />
                </Helmet>
                <MarketShare />
              </>
            ),
          },
          {
            id: RouteID.ProductSpace,
            path: "/explore/productspace",
            element: (
              <>
                <Helmet>
                  <meta property="og:image" content="/og/share_ps.png" />
                </Helmet>
                <ProductSpace />
              </>
            ),
          },
          {
            id: RouteID.Feasibility,
            path: "/explore/feasibility/*",
            element: (
              <>
                <Helmet>
                  <meta property="og:image" content="/og/share_feasibility.png" />
                </Helmet>
                <GrowthPotential />
              </>
            ),
          },
          {
            id: RouteID.TreeMap,
            path: "/explore/treemap",
            element: (
              <>
                <Helmet>
                  <meta property="og:image" content="/og/share_tree.png" />
                </Helmet>
                <Treemap />
              </>
            ),
          },
        ],
      },

      // Atlas UI Component Showcase (dev only)
      ...(process.env.NODE_ENV === "production"
        ? []
        : [
            {
              id: RouteID.AtlasUI,
              path: "/atlas-ui",
              element: <AtlasUIShowcase />,
            },
          ]),

      // Package documentation (dev only)
      ...(process.env.NODE_ENV === "production"
        ? []
        : [
            {
              id: RouteID.PackageDocs,
              path: "/packages",
              element: <PackagesLayout />,
              children: [
                { index: true, element: <PackagesLanding /> },
                { path: "tokens", element: <TokensPage /> },
                { path: "data", element: <PackageDataPage /> },
                { path: "ui", element: <UIPage /> },
                { path: "controls", element: <ControlsPage /> },
                { path: "viz", element: <VizPage /> },
                { path: "architecture", element: <ArchitecturePage /> },
              ],
            },
          ]),

      // Static pages (rewrite)
      {
        id: RouteID.StaticPageEntry,
        path: "",
        element: <StaticPage />,
        children: [
          {
            id: RouteID.Learn,
            path: "/learn",
            element: <Placeholder />,
          },
          {
            id: RouteID.Glossary,
            path: "/glossary",
            element: <Glossary />,
          },
          {
            id: RouteID.FAQ,
            path: "/faq",
            element: <FAQ />,
          },
          {
            id: RouteID.DataDownloads,
            path: "/data-downloads",
            element: <DataPage />,
          },
          {
            id: RouteID.About,
            path: "/about",
            element: <About />,
          },
          {
            id: RouteID.Error,
            path: "*",
            element: <ErrorPage />,
          },
        ],
      },

      // separate to avoid wrapping in StaticPage layout
      {
        id: RouteID.TradeDataMethodology,
        path: "/trade-data-methodology",
        element: <TradeDataMethodologyPage />,
      },
      {
        id: RouteID.TradeDataMethodologyPresentation,
        path: "/trade-data-methodology/presentation",
        element: <TradeDataMethodologyPresentation />,
      },

      // ============================================
      // Country Profiles Routes (lazy-loaded)
      // ============================================

      // Country Profiles (lazy-loaded for code splitting)
      {
        id: RouteID.CountryProfiles,
        path: "/countries/*",
        lazy: () =>
          import("../country-profiles/CountryProfilesWrapper").then((m) => ({
            Component: m.default,
          })),
      },

      // Rankings & Growth Projections (lazy-loaded)
      {
        id: RouteID.CountryRankings,
        path: "/rankings/*",
        lazy: () =>
          import("../pages/rankings/RankingsLayout").then((m) => ({
            Component: m.default,
          })),
      },

      // Press Release (lazy-loaded)
      {
        id: RouteID.GrowthProjections,
        path: "/press-release/*",
        lazy: () =>
          import("../pages/rankings/RankingsLayout").then((m) => ({
            Component: m.default,
          })),
      },

      // Redirect /growth-projections -> /press-release
      {
        path: "/growth-projections",
        element: <Navigate to="/press-release" replace />,
      },

      // Redirect /growth-projections/data -> /rankings/growth-projections
      {
        path: "/growth-projections/data",
        element: <Navigate to="/rankings/growth-projections" replace />,
      },
    ],
  },
];

const recurseRouteChildren = (route: Route | RouteWithChildren) => {
  routesAsSet.add(route);

  if ("children" in route) {
    let children = route.children;
    children?.forEach((child) => recurseRouteChildren(child));
  }
};
export const routesAsSet: Set<Route | RouteWithChildren> = new Set();
routes.forEach((route) => recurseRouteChildren(route));

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const router = sentryCreateBrowserRouter(routes);
